import React, { useMemo, useState, useRef } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import moment from "moment";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { InputSwitch } from "primereact/inputswitch";
import { Message } from "primereact/message";
import { Divider } from "primereact/divider";
import { Toast } from "primereact/toast";
import { confirmDialog } from "primereact/confirmdialog";
import { validarCPF, maskCPF, maskPhone, getAgeCategory, aplicarMascaraDataNascimento, formatNumberCurrency } from "./Utils";
import { agruparTrechos } from "../Constants/quote";
import { replicarHospedes, podeReplicar } from "../Utils/replicarHospedes";
import TermsView from "./TermsModal";
import * as OrderActions from "../Store/Actions/Order";

/**
 * Wizard "Prosseguir com reserva" (Fase 2) — reproduz 1:1 as regras do fluxo de
 * reserva real (hotelBooking.js): busca de dados por CPF (retrievePersonInfoByDocument),
 * responsável pelo pagamento (toggles "é um dos hóspedes"/"é estrangeiro"), hóspedes por
 * quarto (adulto ≥18; criança com idade = data de nascimento; toggles estrangeiro/sem-CPF),
 * validações V1–V9, solicitação especial por quarto e aceite de termos. NÃO seleciona forma
 * de pagamento — só finaliza; o pagamento é feito depois pelo módulo de reservas.
 */

// Helpers LOCAIS do hotelBooking.js (não estão em Utils) — reproduzidos fielmente.
const calcularIdade = (data, targetDate = null) => {
	if (!data) return "";
	let birth = moment(data, "DD/MM/YYYY", true);
	if (!birth.isValid()) birth = moment(data);
	if (!birth.isValid()) return "";
	return targetDate ? moment(targetDate).diff(birth, "years") : moment().diff(birth, "years");
};
const extractNames = (completeName) => {
	if (!completeName) return { firstName: "", lastName: "" };
	const parts = completeName.trim().split(/\s+/);
	return { firstName: parts[0] ?? "", lastName: parts.length > 1 ? parts[parts.length - 1] : "" };
};
// YYYY-MM-DD -> DD/MM/YYYY (para exibição).
const fmtData = (d) => (d && /^\d{4}-\d{2}-\d{2}/.test(d) ? d.slice(0, 10).split("-").reverse().join("/") : (d || ""));

// Formulário de uma pessoa com busca por CPF (responsável e hóspedes).
// `pessoa` = { document, nome, nascimento, estrangeiro, semCpf, dataFetched, manualEdit, fetching }.
const PessoaForm = ({ pessoa, onChange, onBuscar, allowSemCpf, foreignerLabel = "Marque se for estrangeiro" }) => {
	const setP = (patch) => onChange({ ...pessoa, ...patch });
	const estrangeiro = !!pessoa.estrangeiro;
	const semCpf = !!pessoa.semCpf;
	const semBusca = estrangeiro || semCpf; // sem CPF → sem lookup

	const toggleEstrangeiro = (v) =>
		// limpa doc/nome/nascimento; p/ o responsável (tem email/telefone) também limpa (igual hotelBooking).
		setP({ estrangeiro: v, semCpf: false, document: "", nome: "", nascimento: "", manualEdit: v, dataFetched: {}, ...(pessoa.email !== undefined ? { email: "", telefone: "" } : {}) });
	const toggleSemCpf = (v) =>
		setP({ semCpf: v, estrangeiro: false, document: "", nome: "", nascimento: "", manualEdit: v, dataFetched: {} });

	const buscar = async () => {
		setP({ fetching: true });
		const r = await onBuscar(pessoa.document); // valida + chama a API; null se CPF inválido (toast já mostrado)
		if (!r) { setP({ fetching: false }); return; }
		if (r.success) {
			const nome = r.personData?.nome || "";
			const nascimento = r.personData?.nascimento ? r.personData.nascimento.split("-").reverse().join("/") : "";
			setP({ fetching: false, nome, nascimento, dataFetched: r.personData, manualEdit: (!nome || !nascimento) ? true : pessoa.manualEdit });
		} else {
			setP({ fetching: false, manualEdit: true });
		}
	};

	const docPlaceholder = semCpf ? "Informe o número do RG (opcional)" : estrangeiro ? "Informe o número do passaporte" : "Informe o CPF";
	const nomePlaceholder = semBusca ? "Informe o nome completo" : "Automático / Preencha o CPF";
	const idadeTxt = calcularIdade(pessoa.nascimento) ? `${calcularIdade(pessoa.nascimento)} anos` : "";

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			<div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
				<label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: "0.82rem", color: "#666" }}>
					<InputSwitch checked={estrangeiro} onChange={(e) => toggleEstrangeiro(e.value)} /> {foreignerLabel}
				</label>
				{allowSemCpf ? (
					<label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: "0.82rem", color: "#666" }}>
						<InputSwitch checked={semCpf} onChange={(e) => toggleSemCpf(e.value)} /> Marque se não tiver CPF
					</label>
				) : null}
			</div>
			<div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
				<InputText placeholder={docPlaceholder} value={pessoa.document || ""} style={{ flex: 1, minWidth: 180 }}
					onChange={(e) => setP({ document: semBusca ? e.target.value : maskCPF(e.target.value.replace(/\D/g, "")) })} />
				{!semBusca ? <Button label="Buscar" icon="pi pi-search" loading={pessoa.fetching} onClick={buscar} outlined /> : null}
			</div>
			{!semBusca ? <small style={{ color: "#878787", fontSize: "0.72rem" }}>Informe o CPF e selecione buscar para preencher o nome e data de nascimento automaticamente.</small> : null}
			<div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
				<InputText placeholder={nomePlaceholder} value={pessoa.nome || ""} disabled={!pessoa.manualEdit} style={{ flex: 2, minWidth: 180 }}
					onChange={(e) => setP({ nome: e.target.value })} />
				<span style={{ display: "flex", alignItems: "center", gap: 6 }}>
					<InputText placeholder="DD/MM/AAAA" value={pessoa.nascimento || ""} disabled={!pessoa.manualEdit} style={{ width: 130 }}
						onChange={(e) => setP({ nascimento: aplicarMascaraDataNascimento(e.target.value) })} />
					{idadeTxt ? <small style={{ color: "#888" }}>{idadeTxt}</small> : null}
				</span>
			</div>
		</div>
	);
};

const SOLICITACAO_MAX = 150;
const SolicitacaoEspecial = ({ quartoNome, valor, onChange }) => (
	<div style={{ marginTop: 10 }}>
		<div style={{ fontSize: "0.85rem", fontWeight: 600, color: "#2E0249" }}>Quarto: {quartoNome}</div>
		<div style={{ fontSize: "0.85rem", color: "#444", margin: "4px 0" }}>Possui alguma solicitação especial?</div>
		<small style={{ color: "#777", display: "block", marginBottom: 6 }}>
			Inclua os detalhes da sua solicitação especial e vamos encaminhá-los para a propriedade. Importante: a sua solicitação não está garantida e, se você não receber nenhuma resposta, entre em contato diretamente com a propriedade para confirmar.
		</small>
		<InputTextarea value={valor || ""} rows={3} style={{ width: "100%" }}
			placeholder="Descreva suas solicitações especiais. Exemplo: Cama de casal, secador no banheiro, quarto para não fumante e etc.."
			onChange={(e) => { if (e.target.value.length <= SOLICITACAO_MAX) onChange(e.target.value); }} />
		<small style={{ color: (valor || "").length >= SOLICITACAO_MAX ? "#C0392B" : "#999" }}>{(valor || "").length}/{SOLICITACAO_MAX}</small>
	</div>
);

const novaPessoa = (extra = {}) => ({ document: "", nome: "", nascimento: "", estrangeiro: false, semCpf: false, dataFetched: {}, manualEdit: false, fetching: false, ...extra });

const QuoteReserveWizard = (props) => {
	const { quote, onFinalizar, finalizando } = props;
	const toast = useRef(null);
	const trechos = useMemo(() => agruparTrechos(quote), [quote]);
	const sel = quote.selecaoCliente || {};
	const reservas = useMemo(
		() => trechos.map((t) => ({ trecho: t, item: t.opcoes.find((o) => o.id === sel[t.id]) })).filter((r) => r.item),
		[trechos, sel]
	);

	const [responsavel, setResponsavel] = useState(novaPessoa({ email: "", telefone: "" }));
	const [responsavelEhHospede, setResponsavelEhHospede] = useState(true);
	const [solicitacoes, setSolicitacoes] = useState({});
	const [acceptTerm, setAcceptTerm] = useState(false);
	const [termsModal, setTermsModal] = useState(false);

	const [hospedes, setHospedes] = useState(() => {
		const init = {};
		reservas.forEach(({ trecho: t, item: it }) => {
			const adultos = it.pax?.adultos || 1;
			const idades = it.pax?.criancasIdades || [];
			const criancas = it.pax?.criancas || 0;
			const arr = [];
			for (let i = 0; i < adultos; i++) arr.push(novaPessoa({ tipo: "adult" }));
			for (let i = 0; i < criancas; i++) arr.push(novaPessoa({ tipo: "child", age: Number(idades[i]) || 0 }));
			init[t.id] = arr;
		});
		return init;
	});
	const setHospede = (trechoId, i, novo) =>
		setHospedes((prev) => ({ ...prev, [trechoId]: prev[trechoId].map((h, idx) => (idx === i ? novo : h)) }));

	// Replicação de hóspedes entre reservas (pedido do time de vendas, 03/09). A regra mora em
	// Utils/replicarHospedes.js, com teste — aqui só há o gatilho e o aviso do que não coube.
	const [avisosReplicacao, setAvisosReplicacao] = useState([]);
	const origemId = reservas[0]?.trecho?.id;

	const aplicarReplicacao = (destinoId) => {
		const alvo = destinoId ? reservas.filter((r) => r.trecho.id === destinoId) : reservas;
		const { hospedes: novo, relatorio, copiadas } = replicarHospedes(hospedes, alvo, origemId);
		setHospedes(novo);
		setAvisosReplicacao(relatorio);
		toast.current?.show({
			severity: copiadas ? "success" : "warn",
			summary: copiadas ? "Hóspedes replicados" : "Nada a replicar",
			detail: copiadas
				? `Preenchido em ${copiadas} reserva(s).${relatorio.length ? " Confira os avisos — parte não pôde ser copiada." : " Confira antes de finalizar."}`
				: "Preencha os hóspedes da primeira reserva antes de replicar.",
			life: 6000,
		});
	};

	// Busca de dados por CPF (endpoint real) — reproduz fetchHostData: valida antes, chama a
	// action, toasts de erro. Retorna o result do thunk, ou null se o CPF for inválido.
	const buscarDoc = async (documentValue) => {
		const digits = String(documentValue || "").replace(/\D/g, "");
		if (!digits || digits.length !== 11 || !validarCPF(digits)) {
			toast.current?.show({ severity: "warn", summary: "Atenção", detail: "CPF inválido ou incompleto. Verifique e ajuste antes de buscar.", life: 5000 });
			return null;
		}
		try {
			const result = await props.retrievePersonInfoByDocument({ personDocument: digits });
			if (!result?.success) {
				toast.current?.show({ severity: "warn", summary: "Erro", detail: result?.errorMessage || "Erro ao buscar dados do CPF. Tente novamente e caso não retorne, preencha o nome e data de nascimento manualmente.", life: 10000 });
			}
			return result || { success: false };
		} catch (error) {
			toast.current?.show({ severity: "error", summary: "Erro", detail: error?.message || "Erro ao buscar dados do CPF. Tente novamente e caso não retorne, preencha o nome e data de nascimento manualmente pois os campos foram liberados para edição.", life: 10000 });
			return { success: false };
		}
	};

	const setRespForeigner = (v) =>
		setResponsavel((r) => ({ ...r, estrangeiro: v, document: "", nome: "", nascimento: "", email: "", telefone: "", manualEdit: v, dataFetched: {} }));

	// Validações V1–V9 (ordem/mensagens do hotelBooking.js). Retorna a 1ª mensagem de erro ou null.
	const validar = () => {
		// V1 — responsável completo
		if (!responsavel.nome || !responsavel.nascimento || !responsavel.document || !responsavel.email || !responsavel.telefone)
			return "Por favor, preencha todos os dados do responsável pelo pagamento da reserva.";
		// V2 — CPF do responsável (só se não estrangeiro)
		if (!responsavel.estrangeiro && !validarCPF((responsavel.document || "").replace(/\D/g, "")))
			return "O CPF do responsável pela reserva está inválido.";
		// V2b — responsável tem de ser maior de idade (≥ 18)
		if (responsavel.nascimento && calcularIdade(responsavel.nascimento) < 18)
			return "O responsável pelo pagamento deve ter 18 anos ou mais.";
		// loop por reserva/hóspede — V3/V4/V5/V6 + V7 (duplicados na reserva)
		for (const r of reservas) {
			const hosts = hospedes[r.trecho.id] || [];
			for (let i = 0; i < hosts.length; i++) {
				const h = hosts[i]; const n = i + 1;
				if (h.tipo === "child") {
					if (h.nascimento && calcularIdade(h.nascimento) !== h.age)
						return `A idade informada para a criança (hóspede ${n}) não corresponde à data de nascimento. Por favor, verifique os dados informados. Se for o caso, faça nova busca informando a idade correta.`;
				} else if (h.nascimento && calcularIdade(h.nascimento) < 18) {
					return `O hóspede ${n} está definido como adulto, mas tem idade inferior a 18 anos. Verifique os dados informados.`;
				}
				if (!h.nome || !h.nascimento || (!h.semCpf && !h.document))
					return `Por favor, preencha todos os dados obrigatórios do hóspede ${n}.`;
				if (!h.estrangeiro && !h.semCpf && !validarCPF((h.document || "").replace(/\D/g, "")))
					return `O CPF informado para o hóspede ${n} é inválido.`;
			}
			const docs = hosts.filter((h) => !h.semCpf && h.document).map((h) => h.document);
			if (docs.some((d, idx) => docs.indexOf(d) !== idx))
				return "Existe(m) documento(s) repetido(s) entre os hóspedes. Verifique os dados informados.";
		}
		// V8 — responsável é um dos hóspedes (documento tem de bater exatamente)
		if (responsavelEhHospede) {
			const match = Object.values(hospedes).flat().some((h) => h.document && h.document === responsavel.document);
			if (!match) return "O responsável pelo pagamento não corresponde a nenhum dos hóspedes. Verifique os dados informados e certifique-se que o CPF ou Número do passaporte estejam idênticos.";
		}
		// V9 — termos
		if (!acceptTerm) return "É necessário aceitar os termos e condições para prosseguir com a reserva.";
		return null;
	};

	// Monta o payload no shape do createBooking (bookingHosts / bookingPaymentResponsible).
	const paxResponsavel = () => {
		const { firstName, lastName } = extractNames(responsavel.nome || "");
		return {
			isHost: responsavelEhHospede ? 1 : 0,
			isForeigner: responsavel.estrangeiro ? 1 : 0,
			document: responsavel.document || "",
			completeName: responsavel.nome || "",
			firstName: firstName || "Não definido",
			lastName: lastName || "Não definido",
			birthDate: (responsavel.nascimento || "").split("/").reverse().join("-"),
			email: responsavel.email || "",
			mobilePhone: responsavel.telefone || "",
		};
	};
	const paxHost = (h, checkOut) => {
		const { firstName, lastName } = extractNames(h.nome || "");
		const birthDate = (h.nascimento || "").split("/").reverse().join("-");
		return {
			isForeigner: h.estrangeiro ? 1 : 0,
			dontHaveMainDocument: h.semCpf ? 1 : 0,
			document: h.document || "",
			completeName: h.nome || "",
			firstName: firstName || "Não definido",
			lastName: lastName || "Não definido",
			birthDate,
			type: h.tipo || "adult",
			ageOnBookingDate: birthDate ? calcularIdade(h.nascimento, checkOut) : null,
			ageRange: birthDate ? (getAgeCategory(birthDate) || null) : null,
			gender: h.dataFetched?.genero ?? null,
		};
	};

	const enviar = () => {
		const hospedesPorReserva = {};
		reservas.forEach(({ trecho: t, item: it }) => {
			hospedesPorReserva[t.id] = (hospedes[t.id] || []).map((h) => paxHost(h, it.periodo?.checkOut));
		});
		onFinalizar({
			responsavel: paxResponsavel(),
			responsavelEhHospede,
			hospedesPorReserva,
			solicitacoesPorReserva: solicitacoes,
		});
	};

	// Valida na hora (mensagens do fluxo real); só então confirma (all-or-nothing) e envia.
	const finalizar = () => {
		const erro = validar();
		if (erro) { toast.current?.show({ severity: "warn", summary: "Atenção", detail: erro, life: 6000 }); return; }
		const multi = reservas.length > 1;
		confirmDialog({
			header: "Confirmar reserva", icon: "pi pi-check-circle", style: { width: "min(460px, 92vw)" },
			acceptLabel: `Sim, reservar ${reservas.length}`, rejectLabel: "Revisar",
			message: multi
				? `Tem certeza que deseja prosseguir com as ${reservas.length} reservas? Como há mais de uma, se qualquer uma delas falhar, todas serão abortadas e será necessário fazer novamente — não é possível ter apenas uma concluída com sucesso.`
				: "Tem certeza que deseja prosseguir com a reserva?",
			accept: enviar,
		});
	};

	const totalBRL = reservas.reduce((s, r) => s + (Number(r.item.precoBRL) || 0), 0);

	return (
		<div>
			<Toast ref={toast} position="bottom-center" />
			<Message severity="warn" style={{ width: "100%", marginBottom: 12 }}
				text="Vamos re-cotar disponibilidade e preços ao vivo antes de efetivar (podem ocorrer pequenas variações, ocasionalmente). O pagamento é feito depois, pelo módulo de reservas." />

			<strong>Reservas a criar ({reservas.length}) · total {formatNumberCurrency(totalBRL, "BRL")}</strong>
			{reservas.map((r) => (
				<div key={r.trecho.id} style={{ marginTop: 6, color: "#555", fontSize: "0.9rem" }}>
					• {r.item.hotel?.nome} — {r.item.quarto?.nome} · {fmtData(r.item.periodo?.checkIn)} → {fmtData(r.item.periodo?.checkOut)} · {formatNumberCurrency(Number(r.item.precoBRL) || 0, "BRL")}
				</div>
			))}

			<Divider />
			<h3 style={{ margin: "0 0 8px", color: "#2E0249" }}>Responsável pelo pagamento</h3>
			<label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: "0.9rem", marginBottom: 8 }}>
				<InputSwitch checked={responsavelEhHospede} onChange={(e) => setResponsavelEhHospede(e.value)} /> O responsável é um dos hóspedes
			</label>
			{!responsavelEhHospede ? (
				<Message severity="warn" style={{ width: "100%", marginBottom: 10 }}
					text="O responsável pelo pagamento deve ser um dos hóspedes abaixo. Caso não seja, para realizar o pagamento após a reserva, você deverá falar com a Central de atendimento." />
			) : null}
			<PessoaForm pessoa={responsavel} onChange={(p) => setResponsavel(p)} onBuscar={buscarDoc} foreignerLabel="O responsável é estrangeiro" />
			<div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
				<InputText placeholder="Informe o Email do responsável" value={responsavel.email} onChange={(e) => setResponsavel({ ...responsavel, email: e.target.value })} style={{ flex: 2, minWidth: 180 }} />
				<InputText placeholder="Informe o telefone celular do responsável" value={responsavel.telefone} onChange={(e) => setResponsavel({ ...responsavel, telefone: maskPhone(e.target.value) })} style={{ flex: 1, minWidth: 150 }} />
			</div>

			<Divider />
			<h3 style={{ margin: "0 0 8px", color: "#2E0249" }}>Hóspedes</h3>
			{reservas.map((r) => (
				<div key={r.trecho.id} style={{ border: "1px solid #EEE", borderRadius: 10, padding: 12, marginBottom: 10 }}>
					<strong style={{ fontSize: "0.92rem" }}>{r.item.hotel?.nome}</strong>
					<span style={{ color: "#6B6B6B", fontSize: "0.8rem" }}> · {(hospedes[r.trecho.id] || []).length} hóspede(s)</span>
					{(hospedes[r.trecho.id] || []).map((h, i) => (
						<div key={i} style={{ marginTop: 12, paddingTop: i ? 12 : 0, borderTop: i ? "1px dashed #EEE" : "none" }}>
							<div style={{ fontSize: "0.8rem", color: "#888", marginBottom: 4 }}>
								Hóspede {i + 1}: {h.tipo === "child" ? `Criança (${h.age} anos)` : "Adulto"}
							</div>
							<PessoaForm pessoa={h} onChange={(novo) => setHospede(r.trecho.id, i, novo)} onBuscar={buscarDoc} allowSemCpf={h.tipo === "child"} />
						</div>
					))}
					{r.trecho.id === origemId ? (
						<>
							<small style={{ color: "#B26A00", display: "block", marginTop: 8 }}>O primeiro adulto será o responsável pela reserva.</small>
							{reservas.length > 1 ? (
								<Button label={`Replicar para as outras ${reservas.length - 1} reserva(s)`} icon="pi pi-copy" outlined
									disabled={!podeReplicar(hospedes, reservas, origemId)}
									tooltip={podeReplicar(hospedes, reservas, origemId) ? undefined : "Preencha os hóspedes desta reserva primeiro"}
									tooltipOptions={{ position: "top" }}
									style={{ marginTop: 10 }} onClick={() => aplicarReplicacao()} />
							) : null}
						</>
					) : (
						<Button label="Copiar da primeira reserva" icon="pi pi-copy" link
							disabled={!podeReplicar(hospedes, reservas, origemId)}
							style={{ padding: 0, marginTop: 8 }} onClick={() => aplicarReplicacao(r.trecho.id)} />
					)}
					{avisosReplicacao.filter((a) => a.trechoId === r.trecho.id).map((a) => (
						<Message key={a.trechoId} severity="warn" style={{ width: "100%", marginTop: 8, justifyContent: "flex-start", alignItems: "flex-start", textAlign: "left" }}
							text={`Não foi possível copiar tudo aqui: ${a.avisos.join("; ")}.`} />
					))}
					<SolicitacaoEspecial quartoNome={r.item.quarto?.nome} valor={solicitacoes[r.trecho.id]}
						onChange={(v) => setSolicitacoes((prev) => ({ ...prev, [r.trecho.id]: v }))} />
				</div>
			))}

			<Divider />
			<label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: "0.9rem" }}>
				<Checkbox checked={acceptTerm} onChange={(e) => setAcceptTerm(!!e.checked)} />
				<span>Estou de acordo com os <Button label="termos e condições de uso" link style={{ padding: 0, verticalAlign: "baseline" }} onClick={() => setTermsModal(true)} /></span>
			</label>

			<Button label={`Finalizar ${reservas.length} reserva(s)`} icon="pi pi-check" disabled={finalizando} loading={finalizando}
				style={{ width: "100%", marginTop: 12, background: "#814DE5", border: "none" }} onClick={finalizar} />
			<small style={{ color: "#999", display: "block", marginTop: 6 }}>O pagamento é concluído depois, pelo módulo de reservas.</small>

			<TermsView termsModal={termsModal} setTermsModal={setTermsModal} />
		</div>
	);
};

const mapDispatch = (d) => bindActionCreators({ retrievePersonInfoByDocument: OrderActions.retrievePersonInfoByDocument }, d);

export default connect(null, mapDispatch)(QuoteReserveWizard);
